import { END_USER_URL } from '../../../config';
import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { IconButton, AppBar, Box, Toolbar, styled, Badge, Typography } from "@mui/material";
import NavDrawer from "./NavDrawer";
import TransLang from "./TransLang"
import { useTheme } from '@mui/material/styles';
import DarkModeToggle from "../../global/DarkModeToggle";
import { useTranslation } from "react-i18next";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ReactComponent as Heart } from './../../../assets/icons/heart.svg';
import { ReactComponent as Logo } from './../../../assets/logos/LogoWhite.svg';
import { ReactComponent as LogoText } from './../../../assets/logos/LogoTextWhite.svg';
import DropdownMenu from "./DropdownMenu";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useStore } from "../../../context/StoreProvider";
import { useCart } from "../../../context/CartContext";
import ShareButton from "../../global/Share/ShareNavButton";
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { useFavorites } from "../../../context/FavoriteContext";

function NavbarVisitor() {
  const theme = useTheme();
  const [drawer, setDrawer] = useState(false);
  const { slug } = useParams();
  const fallUrl = `${END_USER_URL}/${slug}`;

  const handleShareOpen = (event) => {
    event.stopPropagation();
  };

  // ✅ استخدام useStore بدلاً من useHome
  const { storeData } = useStore();
  
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const StyledLink = styled(Link)(({ theme }) => ({
    textDecoration: "none",
    fontSize: 18,
    textTransform: 'capitalize',
    color: theme.palette.nav.main,
    transition: '0.5s',
    '&:hover': {
      color: theme.palette.nav.main,
      textDecoration: 'underline',
    },
  }));

  const BoxRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer'
  }));

  // ✅ قراءة cart_active من slug-specific key
  const isCart = Number(localStorage.getItem(`cart_active-${slug}`)) === 1 ? true : false;
  const { cartItems } = useCart();
  const { favorites } = useFavorites(); 

  return (
    <>
      <AppBar position="static" elevation={0} sx={{
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
        boxShadow: theme.palette.boxShadow,
        position: 'sticky',
        top: 0,
        zIndex: 999,
        minHeight: 44
      }}>
        <Toolbar disableGutters sx={{ minHeight: 44 }}>
          <Box
            sx={{
              flexGrow: 1,
              marginInlineStart: 1,
              display: { md: "flex", xs: "none" },
              alignItems: "center",
              justifyContent: 'space-evenly',
            }}>
            <StyledLink
              to="/"
              style={{
                textDecoration: "none",
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} >
                <Logo style={{ width: '50px' }} className="svgWhite" />
                <LogoText style={{ width: '100px' }} className="svgWhite" />
              </Box>
            </StyledLink>
            
            {isCart &&
              <BoxRow onClick={() => navigate(`/${slug}/cart`)}>
                <Badge color='primary.main'
                  showZero
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: '#f00',
                    }
                  }} badgeContent={cartItems?.length}>
                  <IconButton sx={{ p: 0 }} >
                    <ShoppingCartOutlinedIcon className="svg" sx={{ fill: '#fff' }} />
                  </IconButton>
                </Badge>
                <Typography>{t('navbarVisitors.card')}</Typography>
              </BoxRow>
            }

            <BoxRow onClick={() => navigate(`/${slug}/favorite`)}>
              <Badge color='primary.main' sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: '#f00',
                }
              }} badgeContent={favorites?.data?.total_favorites_count}>
                <IconButton sx={{ p: 0 }}>
                  <Heart className="svg" />
                </IconButton>
              </Badge>
              <Typography>{t('navbarVisitors.favorite')}</Typography>
            </BoxRow>

            {/* القسم الرئيسي */}
            <BoxRow>
              <ShareButton
                sx={{ flexDirection: 'row', gap: 2 }}
                main_url={fallUrl}
                name={storeData?.name} 
              />
              <Typography>{t('navbarVisitors.share')}</Typography>
            </BoxRow>
            
            <DropdownMenu data={storeData}> {/* ✅ من storeData */}
              <Typography sx={{
                fontSize: 18,
                textTransform: 'capitalize',
                color: theme.palette.primary.text,
                transition: '0.5s',
                '&:hover': {
                  color: theme.palette.primary.text,
                  textDecoration: 'underline',
                },
              }}>{t("more")}</Typography>
              <ExpandMoreIcon />
            </DropdownMenu>
            
            <BoxRow>
              <TransLang lang={storeData?.lang} /> {/* ✅ من storeData */}
              <DarkModeToggle fill={theme.palette.primary.text} />
            </BoxRow>
          </Box>
          
          <Box
            sx={{
              flexGrow: 0,
              display: { md: "none", xs: "flex" },
              justifyContent: "space-between",
              width: "100%",
              alignItems: 'center',
              px: 1
            }}>
            <Box display="flex" alignItems={'center'} sx={{ gap: '6px' }}>
              <IconButton onClick={() => setDrawer(true)}>
                <MoreVertIcon sx={{ fill: '#fff' }} />
              </IconButton>
              <TransLang lang={storeData?.lang} /> 
              <DarkModeToggle fill={theme.palette.primary.text} />
              <IconButton sx={{ py: 0 }} onClick={handleShareOpen}>
                <ShareButton
                  sx={{ flexDirection: 'row', gap: 2, p: '0 !important' }}
                  main_url={fallUrl}
                  name={storeData?.name} 
                />
              </IconButton>
              
              <Badge color='primary.main' sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: '#f00',
                }
              }} badgeContent={favorites?.data?.total_favorites_count}>
                <IconButton sx={{ py: 0 }} onClick={() => navigate(`/${slug}/favorite`)}>
                  <Heart className="svg" style={{ fill: '#fff', width: 22, height: 22 }} />
                </IconButton>
              </Badge>
              
              {isCart &&
                <Badge color='primary.main'
                  showZero
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: '#f00',
                    }
                  }} badgeContent={cartItems?.length}>
                  <IconButton sx={{ py: 0 }} onClick={() => navigate(`/${slug}/cart`)}>
                    <ShoppingCartOutlinedIcon className="svg" style={{ fill: '#fff', width: 22, height: 22 }} />
                  </IconButton>
                </Badge>
              }
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      
      <NavDrawer drawer={drawer} setDrawer={setDrawer} data={storeData} slug={slug} /> 
    </>
  );
}

export default NavbarVisitor;
